import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Hash, TrendingUp, Grid3X3 } from 'lucide-react';
import { DiscoverGrid } from '../components/explore/DiscoverGrid';
import { useAuthStore } from '../store/authStore';
import { searchByHashtag, getTrendingHashtags } from '../lib/discoveryService';
import { Post } from '../types';

export const HashtagPage: React.FC = () => {
  const { hashtag } = useParams<{ hashtag: string }>();
  const { user } = useAuthStore();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [relatedHashtags, setRelatedHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [postCount, setPostCount] = useState(0);

  useEffect(() => {
    if (hashtag) {
      loadHashtagPosts();
      loadRelatedHashtags();
    }
  }, [hashtag]);

  const loadHashtagPosts = async () => {
    if (!hashtag) return;

    setLoading(true);
    try {
      const hashtagPosts = await searchByHashtag(hashtag, 50);
      setPosts(hashtagPosts);
      setPostCount(hashtagPosts.length);
    } catch (error) {
      console.error('Error loading hashtag posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedHashtags = async () => {
    try {
      const trending = await getTrendingHashtags(20);
      // Filter out current hashtag and show related ones
      const related = trending
        .filter(item => item.hashtag.toLowerCase() !== hashtag?.toLowerCase())
        .slice(0, 10);
      setRelatedHashtags(related);
    } catch (error) {
      console.error('Error loading related hashtags:', error);
    }
  };

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-lg">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-screen-lg">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-md mb-6 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <Link to="/explore" className="text-gray-600 mr-4">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex items-center">
                <Hash className="h-5 w-5 mr-2 text-purple-600" />
                <h1 className="text-xl font-bold text-gray-900">#{hashtag}</h1>
              </div>
            </div>
            <div className="flex items-center text-gray-500">
              <Grid3X3 className="h-5 w-5 mr-2" />
              <span className="text-sm">{postCount} posts</span>
            </div>
          </div>

          {/* Hashtag Stats */}
          <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  #{hashtag}
                </h2>
                <p className="text-sm text-gray-600">
                  {postCount} posts • Discover amazing {hashtag} content
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-600">Trending</span>
              </div>
            </div>
          </div>

          {/* Related Hashtags */}
          {relatedHashtags.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Related hashtags</h3>
              <div className="flex flex-wrap gap-2">
                {relatedHashtags.map((item, index) => (
                  <Link
                    key={index}
                    to={`/hashtag/${item.hashtag}`}
                    className="inline-flex items-center px-3 py-1 bg-gray-100 hover:bg-purple-100 text-gray-700 hover:text-purple-700 rounded-full text-sm transition-colors"
                  >
                    <Hash className="h-3 w-3 mr-1" />
                    {item.hashtag}
                    <span className="ml-1 text-xs text-gray-500">({item.count})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Posts Grid */}
        <DiscoverGrid posts={posts} />

        {/* Empty State */}
        {posts.length === 0 && !loading && (
          <div className="bg-white border border-gray-200 rounded-md p-8 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <Hash className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No posts found for #{hashtag}
            </h3>
            <p className="text-gray-500 mb-4">
              Be the first to share content with this hashtag!
            </p>
            <Link 
              to="/create" 
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              Create Post
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};